import {
  Badge,
  Button,
  Flex,
  Menu,
  Portal,
  Table,
  Text,
} from '@chakra-ui/react'
import React from 'react'

import { useReportingI18n } from '../../../i18n'
import type {
  QueueAgent,
  QueueDashboardAgentActions,
  QueueDashboardCustomStatus,
  QueueDashboardExtension,
} from '../types'
import { AGENT_STATE_DISPLAY } from '../types'

const QueueAgentStatus = {
  InQueue: 'IN_QUEUE',
  Offline: 'OFFLINE',
} as const

type QueueAgentStatusValue =
  (typeof QueueAgentStatus)[keyof typeof QueueAgentStatus]

const isQueueAgentStatus = (value: unknown): value is QueueAgentStatusValue =>
  value === QueueAgentStatus.InQueue || value === QueueAgentStatus.Offline

type AgentWithCustomStatus = QueueAgent & {
  _customDisplayStatus?: string
}

export interface AgentRowProps {
  agent: AgentWithCustomStatus
  isActive: boolean
  index: number
  customStatuses?: QueueDashboardCustomStatus[]
  extensions?: QueueDashboardExtension[]
  showActions?: boolean
  agentActions?: QueueDashboardAgentActions
}

const STATUS_BADGE_COLORS: Record<string, { bg: string; color: string }> = {
  Ready: { bg: 'green.subtle', color: 'green.fg' },
  'On Call': { bg: 'purple.subtle', color: 'purple.fg' },
  Ringing: { bg: 'orange.subtle', color: 'orange.fg' },
  Available: { bg: 'green.subtle', color: 'green.fg' },
  'Wrap-Up': { bg: 'orange.subtle', color: 'orange.fg' },
  Break: { bg: 'orange.subtle', color: 'orange.fg' },
  'On Call - Inbound': { bg: 'purple.subtle', color: 'purple.fg' },
  'On Call - Outbound': { bg: 'purple.subtle', color: 'purple.fg' },
  'Project Work': { bg: 'purple.subtle', color: 'purple.fg' },
  Offline: { bg: 'gray.subtle', color: 'gray.fg' },
}

const getTranslatedStatus = (
  status: string,
  text: (key: string, fallback: string) => string,
): string => {
  const statusMap: Record<string, string> = {
    Ready: text('queueDashboard.status.ready', 'Ready'),
    'On Call': text('queueDashboard.status.onCall', 'On Call'),
    Ringing: text('queueDashboard.status.ringing', 'Ringing'),
    Available: text('queueDashboard.status.available', 'Available'),
    Break: text('queueDashboard.status.break', 'Break'),
    Offline: text('queueDashboard.agentRow.offline', 'Offline'),
  }
  return statusMap[status] || status
}

const Tr = Table.Row
const Td = Table.Cell

const formatTalkTime = (seconds: number | null | undefined): string => {
  if (seconds == null || isNaN(seconds) || seconds < 0) {
    return '0 min, 0 sec'
  }
  const hours = Math.floor(seconds / 3600)
  const mins = Math.floor((seconds % 3600) / 60)
  const secs = Math.floor(seconds % 60)
  if (hours > 0) {
    return `${hours} hr, ${mins} min, ${secs} sec`
  }
  return `${mins} min, ${secs} sec`
}

const formatTimestamp = (epoch: number, locale: string): string => {
  if (!epoch) return '-'
  const date = new Date(epoch * 1000)
  return date.toLocaleString(locale, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

const AgentRow: React.FC<AgentRowProps> = ({
  agent,
  isActive,
  customStatuses = [],
  extensions = [],
  showActions = false,
  agentActions,
}) => {
  const { locale, text } = useReportingI18n()

  // Extract extension UUID from agent string (format: ext-uuid@pbx-uuid)
  const extractExtensionUuid = (agentStr: string): string | undefined => {
    if (!agentStr) return undefined
    const atIndex = agentStr.indexOf('@')
    return atIndex > 0 ? agentStr.substring(0, atIndex) : undefined
  }

  const extensionUuid = extractExtensionUuid(agent.agent)
  const extension = extensions.find((ext) => ext.uuid === extensionUuid)

  const handleQueueError = (error: unknown) => {
    agentActions?.onQueueUpdateError?.(error)
  }

  const handleStatusChange = async (value: QueueAgentStatusValue | string) => {
    if (!extension?.id || !extensionUuid) {
      // This is a client-side data gap (the agent's extension/uuid couldn't be
      // resolved locally), not a queue-membership problem, so surface a generic
      // error rather than the 404 "add to a Call Queue" message.
      handleQueueError({
        message: 'Unable to resolve extension for agent',
      })
      return
    }

    switch (value) {
      case QueueAgentStatus.InQueue:
        try {
          await agentActions?.onQueueLogin?.(extension.id)
          await agentActions?.onUpdateAgentStatus?.(null, extensionUuid, true)
          agentActions?.onStatusUpdated?.()
        } catch (error: unknown) {
          handleQueueError(error)
        }
        break
      case QueueAgentStatus.Offline:
        try {
          await agentActions?.onQueueLogout?.(extension.id)
          await agentActions?.onUpdateAgentStatus?.(null, extensionUuid, false)
          agentActions?.onStatusUpdated?.()
        } catch (error: unknown) {
          handleQueueError(error)
        }
        break
      default:
        try {
          await agentActions?.onQueueLogout?.(extension.id)
          if (!isQueueAgentStatus(value)) {
            await agentActions?.onUpdateAgentStatus?.(
              value,
              extensionUuid,
              false,
            )
            agentActions?.onStatusUpdated?.()
          }
        } catch (error: unknown) {
          handleQueueError(error)
        }
        break
    }
  }

  const handleSelect = (value: QueueAgentStatusValue | string) => {
    void handleStatusChange(value)
  }

  // Determine the current selected status value for the menu
  const getSelectedValue = (): QueueAgentStatusValue => {
    if (isActive) {
      return QueueAgentStatus.InQueue
    }
    return QueueAgentStatus.Offline
  }

  // Get menu items based on current status
  const getMenuItems = () => {
    const items: { value: QueueAgentStatusValue | string; label: string }[] = []
    const selectedValue = getSelectedValue()

    if (selectedValue !== QueueAgentStatus.InQueue) {
      items.push({
        value: QueueAgentStatus.InQueue,
        label: text('queueStatusMenu.joinQueue', 'Join Queue'),
      })
    }

    if (
      selectedValue === QueueAgentStatus.InQueue ||
      !isQueueAgentStatus(selectedValue)
    ) {
      items.push({
        value: QueueAgentStatus.Offline,
        label: text('queueStatusMenu.goOffline', 'Go Offline'),
      })
    }

    // Add custom statuses
    const onlineStatuses = (customStatuses ?? []).filter(
      (status) =>
        status.availability === 'Online' && status.id !== selectedValue,
    )
    const awayStatuses = (customStatuses ?? []).filter(
      (status) => status.availability === 'Away' && status.id !== selectedValue,
    )

    onlineStatuses.forEach((status) => {
      items.push({ value: status.id, label: status.name })
    })
    awayStatuses.forEach((status) => {
      items.push({ value: status.id, label: status.name })
    })

    return items
  }

  let displayState: string
  const customDisplayStatus = agent._customDisplayStatus
  if (customDisplayStatus) {
    displayState = customDisplayStatus
  } else {
    displayState = AGENT_STATE_DISPLAY[agent.state]
    if (agent.status === 'on_break') {
      displayState = 'Break'
    } else if (
      agent.status === 'available' &&
      agent.state === 'idle' &&
      isActive
    ) {
      displayState = 'Available'
    }
  }
  const translatedDisplayState = getTranslatedStatus(displayState, text)

  const badgeColors =
    STATUS_BADGE_COLORS[displayState] || STATUS_BADGE_COLORS.Offline
  const loggedInTime = formatTimestamp(agent.lastStatusChange ?? 0, locale)
  const talkTime = formatTalkTime(agent.talkTime ?? 0)
  const lastOfferedTime = formatTimestamp(agent.lastOfferedCall ?? 0, locale)
  const callsAnswered = Number(agent.callsAnswered ?? 0)
  const noAnswerCount = Number(agent.noAnswerCount ?? 0)

  const cellStyle = {
    padding: '12px 16px',
    fontSize: '0.875rem',
    color: 'fg.default',
  }

  // Render the status change menu
  const renderStatusMenu = () => {
    const menuItems = getMenuItems()

    return (
      <Menu.Root
        positioning={{
          placement: 'bottom-start',
          flip: true,
          strategy: 'fixed',
        }}
      >
        <Menu.Trigger asChild>
          <Button
            size="xs"
            variant="outline"
            colorPalette="neutral"
            fontWeight="500"
          >
            {text('queueDashboard.agentRow.changeStatus', 'Change Status')} ▾
          </Button>
        </Menu.Trigger>
        <Portal>
          <Menu.Positioner>
            <Menu.Content minW="180px">
              {menuItems.map((item) => (
                <Menu.Item
                  key={String(item.value)}
                  value={String(item.value)}
                  onClick={() => handleSelect(item.value)}
                  py="8px"
                  px="12px"
                  cursor="pointer"
                  _hover={{ bg: 'bg.muted' }}
                >
                  {item.label}
                </Menu.Item>
              ))}
            </Menu.Content>
          </Menu.Positioner>
        </Portal>
      </Menu.Root>
    )
  }

  if (isActive) {
    return (
      <Tr
        data-testid={`agent-row-${agent.extension}`}
        data-active="true"
        borderBottom="1px solid"
        borderColor="border.default"
        _last={{ borderBottom: 'none' }}
      >
        <Td {...cellStyle}>
          <Flex alignItems="center" gap="8px">
            <Text color="fg.default" fontWeight="500">
              {agent.name}
            </Text>
          </Flex>
        </Td>

        <Td {...cellStyle}>
          <Badge
            bg="gray.subtle"
            color="gray.fg"
            px="8px"
            py="2px"
            borderRadius="md"
            fontSize="0.75rem"
          >
            {agent.extension}
          </Badge>
        </Td>

        <Td {...cellStyle}>{loggedInTime}</Td>
        <Td {...cellStyle}>{talkTime}</Td>
        <Td {...cellStyle}>{callsAnswered}</Td>
        <Td {...cellStyle}>{noAnswerCount}</Td>

        <Td {...cellStyle}>
          <Badge
            bg={badgeColors.bg}
            color={badgeColors.color}
            px="12px"
            py="4px"
            borderRadius="full"
            fontSize="0.75rem"
            fontWeight="500"
          >
            {translatedDisplayState}
          </Badge>
        </Td>

        <Td {...cellStyle}>{lastOfferedTime}</Td>

        {showActions && (
          <Td {...cellStyle}>
            <Flex
              alignItems="center"
              gap="0.5rem"
              data-testid={`agent-action-${agent.extension}`}
            >
              {renderStatusMenu()}
            </Flex>
          </Td>
        )}
      </Tr>
    )
  }

  return (
    <Tr
      data-testid={`agent-row-${agent.extension}`}
      data-active="false"
      borderBottom="1px solid"
      borderColor="border.default"
      _last={{ borderBottom: 'none' }}
    >
      <Td {...cellStyle}>
        <Flex alignItems="center" gap="8px">
          <Text color="fg.default" fontWeight="500">
            {agent.name}
          </Text>
        </Flex>
      </Td>

      <Td {...cellStyle}>
        <Badge
          bg="gray.subtle"
          color="gray.fg"
          px="8px"
          py="2px"
          borderRadius="md"
          fontSize="0.75rem"
        >
          {agent.extension}
        </Badge>
      </Td>

      <Td {...cellStyle}>{loggedInTime}</Td>

      <Td {...cellStyle}>
        <Badge
          bg="gray.subtle"
          color="gray.fg"
          px="12px"
          py="4px"
          borderRadius="full"
          fontSize="0.75rem"
          fontWeight="500"
        >
          {text('queueDashboard.agentRow.offline', 'Offline')}
        </Badge>
      </Td>

      {showActions && (
        <Td {...cellStyle}>
          <Flex
            alignItems="center"
            gap="0.5rem"
            data-testid={`agent-action-${agent.extension}`}
          >
            {renderStatusMenu()}
          </Flex>
        </Td>
      )}
    </Tr>
  )
}

export default AgentRow
